import React from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleQuestion } from '@fortawesome/free-solid-svg-icons';
import BreakdownChart, { BreakdownDataPoint } from './BreakdownChart';
import DonutChart from './DonutChart';

interface BreakdownChartWidgetProps {
    title: string;
    subtitle?: string;
    tooltipText?: string;
    data: BreakdownDataPoint[];
    groupLabels: Record<string, string>;
    loading?: boolean;
    height?: number;
    color?: string;
    yFormatter?: (value: number) => string;
    // Optional extra class for the title, used to match the title size elsewhere without affecting other usages
    titleClassName?: string;
    // 'bar' shows a value per category, 'donut' shows each category's share of the total
    variant?: 'bar' | 'donut';
    // 'side' places the donut legend to the right instead of below, for widgets wider than 1 column
    legendPosition?: 'bottom' | 'side';
}

const BreakdownChartWidget: React.FC<BreakdownChartWidgetProps> = ({
    title, subtitle, tooltipText, data, groupLabels, loading, height, color, yFormatter, titleClassName, variant = 'bar', legendPosition,
}) => {
    const { t } = useTranslation();

    // A breakdown chart is only truly empty when every group has a zero value
    const hasData = data.some(point => point.value > 0);

    return (
        <div className='metric-chart-widget'>
            <div className='metric-chart-header'>
                <h6 className={titleClassName}>
                    {title}
                    {tooltipText && (
                        <span className='tooltip-icon'>
                            <FontAwesomeIcon icon={faCircleQuestion} />
                            <span className='tooltip tooltip-right'>{tooltipText}</span>
                        </span>
                    )}
                </h6>
                {subtitle && <span className='metric-chart-subtitle'>{subtitle}</span>}
            </div>
            {/* Show a loader, an empty state, or the chart itself, matching the given height so nothing jumps once it arrives */}
            {loading ? (
                <div className='metric-chart-loading' style={height ? { height } : undefined}>
                    <div className='loader' />
                </div>
            ) : !hasData ? (
                <div className='metric-chart-empty' style={height ? { height } : undefined}>{t('general.no_data_available')}</div>
            ) : variant === 'donut' ? (
                <DonutChart data={data} groupLabels={groupLabels} height={height} valueFormatter={yFormatter} legendPosition={legendPosition} />
            ) : (
                <BreakdownChart data={data} groupLabels={groupLabels} height={height} color={color} yFormatter={yFormatter} />
            )}
        </div>
    );
};

export default BreakdownChartWidget;
